import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import useI18n from '../../i18n/useI18n'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import { Actions as UiActions } from '../../redux/ui/reducers'
import general from '../../utils/general'
import RoleBasedAccess from '../../components/role-based-access'
import { UserTypeEnum } from '../../utils/enums'
import {
  HeroSection,
  UniversityInfo,
  ImageSlider,
  InfoAccordions,
  ProgramFilters,
  ApplicationForm,
  ApplicationOptionModal
} from './components'
import ProgramsTable from './components/ProgramsTable'
import './detail.scss'

const UniversityDetail = () => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, language } = useI18n()
  const userInfo = useSelector((state) => state.auth?.userInfo || {})
  const [loading, setLoading] = useState(true)
  const [university, setUniversity] = useState(null)
  const [universityDetail, setUniversityDetail] = useState(null)
  const [programs, setPrograms] = useState([])
  const [programsLoading, setProgramsLoading] = useState(false)
  const [filters, setFilters] = useState({
    degreeLevel: null,
    languages: [],
    search: ''
  })
  const [selectedProgram, setSelectedProgram] = useState(null)
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false)
  const [isApplicationOptionModalOpen, setIsApplicationOptionModalOpen] = useState(false)
  const [programForApplication, setProgramForApplication] = useState(null)

  // Programs'tan unique derece ve dil değerlerini çıkar
  const availableDegreeLevels = useMemo(() => {
    const degrees = new Set()
    programs.forEach(program => {
      if (program.degree) {
        degrees.add(program.degree)
      }
    })
    return Array.from(degrees)
  }, [programs])

  const availableLanguages = useMemo(() => {
    const langs = new Set()
    programs.forEach(program => {
      if (program.language) {
        langs.add(program.language)
      }
    })
    return Array.from(langs)
  }, [programs])

  // Base university ve detail verisini birleştir
  const mergedUniversity = useMemo(() => {
    if (!university) return null

    // Detail API'den gelen verileri frontend alanlarına eşle
    const detailMapped = universityDetail ? {
      coverImageUrl: universityDetail.bannerImage || university.coverImageUrl,
      summary: universityDetail.summary,
      about: universityDetail.about,
      videoUrl: universityDetail.videoLink ? convertToEmbedUrl(universityDetail.videoLink) : university.videoUrl,
      applicationInfo: universityDetail.applicationInfo,
      campusLife: universityDetail.campusInfo,
      ranking: universityDetail.rankingInfo,
      scholarships: universityDetail.scholarshipInfo,
    } : {}

    return {
      ...university,
      ...detailMapped,
      // summary varsa description olarak da kullan (eğer description yoksa)
      description: university.description || universityDetail?.summary || university.description,
    }
  }, [university, universityDetail])

  // Localized university name
  const localizedUniversityName = useMemo(() => {
    return general.getLocaleValue(mergedUniversity, 'name', language)
  }, [mergedUniversity, language])

  // YouTube/Video linkini embed formatına çevir
  function convertToEmbedUrl(url) {
    if (!url) return null

    // YouTube watch linkini embed'e çevir
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0]
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
    }

    // YouTube kısa linkini embed'e çevir
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0]
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
    }

    // Zaten embed formatındaysa veya başka bir video servisi ise olduğu gibi döndür
    return url
  }

  // Sayfa açıldığında sol menüyü otomatik kapat
  useEffect(() => {
    dispatch(UiActions.closeSidebar())
  }, [dispatch])

  useEffect(() => {
    if (id) {
      fetchUniversityBase()
      fetchUniversityDetailInfo()
      fetchPrograms()
    }
  }, [id])

  // Base üniversite verilerini çek
  const fetchUniversityBase = () => {
    setLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `universities/${id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const universityData = data?.data || data
          setUniversity(universityData)
          setLoading(false)
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('UNIVERSITY_DETAIL_LOAD_ERROR'))
          navigate('/universities')
          setLoading(false)
        },
        callback: (apiCall) => {
          if (apiCall?.error) {
            setLoading(false)
          }
        }
      })
    )
  }

  // Detail API'den ek bilgileri çek
  const fetchUniversityDetailInfo = () => {
    dispatch(
      ApiCallActions.Get({
        url: `universities/${id}/detail`,
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const detailData = data?.data || data
          setUniversityDetail(detailData)
        },
        onError: () => {
          // Detail endpoint henüz oluşturulmamışsa null döner, hata vermez
          setUniversityDetail(null)
        }
      })
    )
  }

  const fetchPrograms = () => {
    setProgramsLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `universities/${id}/programs`,
        query: { page: 1, limit: 999 },
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const programsData = data?.data || data || []

          setPrograms(Array.isArray(programsData) ? programsData : [])
          setProgramsLoading(false)
        },
        onError: () => {
          // Programs endpoint might not exist, set empty array
          setPrograms([])
          setProgramsLoading(false)
        }
      })
    )
  }

  const backDestination = useMemo(() => {
    const fromState = location.state?.from
    if (typeof fromState === 'string' && fromState.trim().length > 0) {
      return fromState
    }
    return '/universities'
  }, [location.state])

  const handleBackClick = () => {
    dispatch(UiActions.openSidebar())
    navigate(backDestination)
  }

  const handleHeroApplyClick = () => {
    // Check university type
    const uniType = mergedUniversity?.universityType || mergedUniversity?.university_type

    if (uniType === 'public') {
      // Devlet üniversitesi: Seçenek modalı aç
      setSelectedProgram(null)
      setIsApplicationOptionModalOpen(true)
    } else {
      // Özel üniversite: Direkt başvuru formu aç
      setSelectedProgram(null)
      setIsApplicationFormOpen(true)
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleApplyClick = (program) => {
    // Check university type
    const uniType = mergedUniversity?.universityType || mergedUniversity?.university_type

    setSelectedProgram(program)
    setProgramForApplication(program) // Program'ı direkt set et

    if (uniType === 'public') {
      // Devlet üniversitesi: Seçenek modalı aç
      setIsApplicationOptionModalOpen(true)
    } else {
      // Özel üniversite: Direkt başvuru formu aç
      setIsApplicationFormOpen(true)
    }
  }

  const handleApplicationFormClose = () => {
    setIsApplicationFormOpen(false)
    setSelectedProgram(null)
    setProgramForApplication(null)
  }

  const handleApplicationOptionModalClose = () => {
    setIsApplicationOptionModalOpen(false)
    setSelectedProgram(null)
  }

  const handleApplicationOptionSelect = (option) => {
    if (option === 'agency') {
      // Acente üzerinden başvuru seçildi, formu aç
      // programForApplication zaten set edilmiş olmalı
      setIsApplicationFormOpen(true)
    }
    // 'website' seçilirse modal içinde zaten yönlendirme yapılıyor
  }

  const handleBulkUpdateClick = () => {
    navigate(`/programs/new?universityId=${id}&tab=bulk-fees`, {
      state: { universityName: mergedUniversity?.name }
    })
  }

  const handleBackFromBulkUpdate = () => {
    // This function might not be needed anymore if we navigate away, 
    // but keeping it if we need logic to refresh when coming back (useEffect)
    fetchPrograms()
  }

  // Prepare user data for pre-filling the application form
  const getUserData = () => {
    if (!userInfo) return null

    return {
      firstName: userInfo.firstName || '',
      lastName: userInfo.lastName || '',
      email: userInfo.email || '',
      phone: userInfo.phone || '',
      userType: userInfo.userType || '',
      id: userInfo.id || '',
      agentId: userInfo.agentId || userInfo.agent?.id || '',
      // Additional fields can be added if they exist in userInfo
      fatherName: userInfo.fatherName || '',
      motherName: userInfo.motherName || '',
      phoneCountryCode: userInfo.phoneCountryCode || '+90',
      citizenship: userInfo.citizenship || '',
      residenceCountry: userInfo.residenceCountry || '',
      citizenshipStatus: userInfo.citizenshipStatus || ''
    }
  }

  // Prepare images array for slider
  const getSliderImages = () => {
    if (!mergedUniversity) return []

    const images = []

    // Add cover image if exists
    if (mergedUniversity.coverImageUrl) {
      images.push({
        url: mergedUniversity.coverImageUrl,
        alt: t('UNIVERSITY_DETAIL_COVER_IMAGE_ALT', {
          name: mergedUniversity.name || t('UNIVERSITY_DETAIL_TITLE')
        })
      })
    }

    // Add gallery images if exists
    if (mergedUniversity.galleryImages && Array.isArray(mergedUniversity.galleryImages)) {
      images.push(...mergedUniversity.galleryImages)
    }

    // Add campus images if exists
    if (mergedUniversity.campusImages && Array.isArray(mergedUniversity.campusImages)) {
      images.push(...mergedUniversity.campusImages)
    }

    // Eğer hiç görsel yoksa örnek görseller ekle
    if (images.length === 0) {
      const sampleImages = [
        {
          url: '/assets/images/image-gallery/1.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 1 })
        },
        {
          url: '/assets/images/image-gallery/2.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 2 })
        },
        {
          url: '/assets/images/image-gallery/3.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 3 })
        },
        {
          url: '/assets/images/image-gallery/4.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 4 })
        },
        {
          url: '/assets/images/image-gallery/5.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 5 })
        },
        {
          url: '/assets/images/image-gallery/4.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 4 })
        },
        {
          url: '/assets/images/image-gallery/5.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 5 })
        },
        {
          url: '/assets/images/image-gallery/4.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 4 })
        },
        {
          url: '/assets/images/image-gallery/5.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 5 })
        },
        {
          url: '/assets/images/image-gallery/6.jpg',
          alt: t('UNIVERSITY_DETAIL_SAMPLE_CAMPUS_VIEW_ALT', { index: 6 })
        }
      ]
      images.push(...sampleImages)
    }

    return images
  }

  if (loading) {
    return (
      <section className="content">
        <div className="block-header">
          <div className="row">
            <div className="col-lg-7 col-md-6 col-sm-12 text-left">
              <h2>
                {t('UNIVERSITY_DETAIL_TITLE')}
                <small>{t('COMMON_LOADING')}</small>
              </h2>
            </div>
            <div className="col-lg-5 col-md-6 col-sm-12">
              <ul className="breadcrumb float-md-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">
                    <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('UNIVERSITY_DETAIL_BREADCRUMB_HOME')}
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/universities">{t('UNIVERSITIES_BREADCRUMB_LIST')}</Link>
                </li>
                <li className="breadcrumb-item active">{t('UNIVERSITY_DETAIL_BREADCRUMB_DETAIL')}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-12 text-center p-5">
              <i className="zmdi zmdi-refresh zmdi-hc-spin" style={{ fontSize: '48px', color: '#667eea' }}></i>
              <p style={{ marginTop: '20px', fontSize: '18px', color: '#666' }}>{t('COMMON_LOADING')}</p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  if (!mergedUniversity) {
    return (
      <section className="content">
        <div className="block-header">
          <div className="row">
            <div className="col-lg-7 col-md-6 col-sm-12 text-left">
              <h2>
                {t('UNIVERSITY_DETAIL_TITLE')}
                <small>{t('UNIVERSITY_DETAIL_NOT_FOUND_SUBTITLE')}</small>
              </h2>
            </div>
            <div className="col-lg-5 col-md-6 col-sm-12">
              <ul className="breadcrumb float-md-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">
                    <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('UNIVERSITY_DETAIL_BREADCRUMB_HOME')}
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/universities">{t('UNIVERSITIES_BREADCRUMB_LIST')}</Link>
                </li>
                <li className="breadcrumb-item active">{t('UNIVERSITY_DETAIL_BREADCRUMB_DETAIL')}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-12 text-center p-5">
              <i className="zmdi zmdi-alert-circle" style={{ fontSize: '48px', color: '#f44336', marginBottom: '20px' }}></i>
              <p style={{ fontSize: '18px', color: '#666', marginBottom: '30px' }}>{t('UNIVERSITY_DETAIL_NOT_FOUND_MESSAGE')}</p>
              <Link to="/universities" className="btn btn-primary">
                {t('UNIVERSITY_DETAIL_BACK_TO_LIST')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {localizedUniversityName || t('UNIVERSITY_DETAIL_TITLE')}
              <small>{t('UNIVERSITY_DETAIL_SUBTITLE_INFO')}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <button
              className="btn btn-white btn-icon btn-round float-right m-l-10"
              type="button"
              onClick={handleBackClick}
              title={t('UNIVERSITY_DETAIL_BACK_TITLE')}
            >
              <i className="zmdi zmdi-arrow-left"></i>
            </button>
            <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN]}>
              <button
                className="btn btn-white btn-icon btn-round float-right m-l-10"
                type="button"
                onClick={() => navigate(`/universities/${id}/edit-detail`)}
                title={t('UNIVERSITY_DETAIL_EDIT_TITLE')}
              >
                <i className="zmdi zmdi-edit"></i>
              </button>
            </RoleBasedAccess>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <Link to="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('UNIVERSITY_DETAIL_BREADCRUMB_HOME')}
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/universities">{t('UNIVERSITIES_BREADCRUMB_LIST')}</Link>
              </li>
              <li className="breadcrumb-item active">{t('UNIVERSITY_DETAIL_BREADCRUMB_DETAIL')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <HeroSection
        university={mergedUniversity}
        onApplyClick={handleHeroApplyClick}
      />

      {/* University Info Section */}
      <UniversityInfo university={mergedUniversity} />

      {/* geçici süreç için kapatıldı - Image Slider Section */}
      {/* <ImageSlider images={getSliderImages()} /> */}

      {/* Info Accordions Section */}
      <InfoAccordions university={mergedUniversity} />

      {/* Program Filters Section */}
      <ProgramFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        availableDegreeLevels={availableDegreeLevels}
        availableLanguages={availableLanguages}
        onBulkUpdateClick={handleBulkUpdateClick}
      />

      {/* Programs Table Section */}
      <ProgramsTable
        programs={programs}
        loading={programsLoading}
        filters={filters}
        universityName={localizedUniversityName}
        onApplyClick={handleApplyClick}
      />

      {/* Application Option Modal - For Public Universities */}
      <ApplicationOptionModal
        isOpen={isApplicationOptionModalOpen}
        onClose={handleApplicationOptionModalClose}
        onSelectOption={handleApplicationOptionSelect}
        universityWebsite={mergedUniversity?.websiteUrl || mergedUniversity?.website_url}
        universityName={localizedUniversityName}
      />

      {/* Application Form Modal */}
      <ApplicationForm
        isOpen={isApplicationFormOpen}
        onClose={handleApplicationFormClose}
        program={programForApplication || selectedProgram}
        universityName={localizedUniversityName}
        userData={getUserData()}
        universityId={id}
      />
    </section >
  )
}

export default UniversityDetail
